// dev/src/services/api.js
import axios from 'axios';
import { auth } from '../config/firebase';

// ==========================================
// ☁️ 1. Google Cloud Storage 配置 (新增)
// ==========================================
// 您的 Bucket 公开访问地址 (注意末尾带斜杠)
export const STORAGE_BASE_URL = "https://storage.googleapis.com/vocabrun.firebasestorage.app/";

// ==========================================
// 🖼️ 2. 图片路径处理工具函数 (新增)
// ==========================================
/**
 * 将数据库中的相对路径转换为完整的云端 URL
 * @param {string} path - 数据库存的路径 (如 "images/vocabulary/apple.jpg")
 * @returns {string} - 可直接在 <img src> 中使用的完整链接
 */
export const getImgUrl = (path) => {
  // 空值保护：path 为空时返回空字符串，避免 TypeError
  if (!path) return '';

  // 2. 如果已经是完整链接 (比如用户头像来自 Google，或者 Blob 预览图)，直接返回
  if (path.startsWith('http') || path.startsWith('blob:') || path.startsWith('data:')) {
    return path;
  }

  // 3. 处理相对路径：拼接云端前缀
  // 去掉开头的 / 防止双斜杠 (例如 path 是 "/images/..." 变成 "images/...")
  const cleanPath = path.startsWith('/') ? path.substring(1) : path;
  
  return `${STORAGE_BASE_URL}${cleanPath}`;
};

const API_BASE_URL = import.meta.env.PROD
  ? '/api'
  : 'http://localhost:8080/api';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 🛠️ 等待 Auth 初始化完成的辅助函数 (解决 401 问题)
const waitForAuthReady = () => {
  return new Promise((resolve) => {
    if (auth.currentUser) {
      resolve(auth.currentUser);
      return;
    }
    const unsubscribe = auth.onAuthStateChanged((user) => {
      unsubscribe();
      resolve(user);
    });
  });
};

// 请求拦截器
apiClient.interceptors.request.use(
  async (config) => {
    try {
      let user = auth.currentUser;
      
      if (!user) {
        user = await waitForAuthReady();
      }

      if (user) {
        const token = await user.getIdToken();
        if (config.headers && config.headers.set) {
             config.headers.set('Authorization', `Bearer ${token}`);
        } else {
             config.headers['Authorization'] = `Bearer ${token}`;
        }
      }
    } catch (error) {
      console.error('❌ [API] 获取 Token 失败:', error);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器
apiClient.interceptors.response.use(
  (response) => response.data,
  (error) => {
    if (error.response) {
      const { status, data } = error.response;
      console.error(`❌ [API Error] ${status}:`, data?.error || error.message);
      throw new Error(data?.error || `Request failed with status ${status}`);
    } else if (error.request) {
      console.error('❌ [Network Error] 无响应:', error.message);
      throw new Error('Network error. Please check backend server.');
    } else {
      console.error('❌ [Request Error]', error.message);
      throw new Error(error.message);
    }
  }
);

class ApiService {
  constructor() {
    this.client = apiClient;
  }

  async request(endpoint, options = {}) {
    return this.client.request({ url: endpoint, ...options });
  }

  // ==========================================
  // 1. Auth & Profile (认证与个人资料)
  // ==========================================
  async register(userData) { return this.client.post('/auth/register', userData); }
  async login(idToken) { return this.client.post('/auth/login', { idToken }); }
  async getProfile() { return this.client.get('/users/profile'); }
  async updateProfile(data) { return this.client.put('/users/profile', data); }

  // ==========================================
  // 2. Admin & Users (用户管理)
  // ==========================================
  async getAllUsers(params = {}) {
    // params: { page, limit, role }
    return this.client.get('/users', { params });
  }

  async approveUser(userId, status) {
    return this.client.put(`/users/${userId}/approve`, { status });
  }

  async updateUser(userId, data) {
    return this.client.put(`/users/${userId}`, data);
  }

  async deleteUser(userId) {
    return this.client.delete(`/users/${userId}`);
  }

  // ==========================================
  // 3. Vocabulary (词库管理)
  // ==========================================
  async getVocabulary(params = {}) {
    // params: { page, limit, status, level }
    return this.client.get('/vocabulary', { params });
  }
  
  async createVocabulary(data) { return this.client.post('/vocabulary', data); }
  async updateVocabulary(id, data) { return this.client.put(`/vocabulary/${id}`, data); }
  
  async getRandomVocabulary(level = 'all', count = 10) {
    return this.client.get(`/vocabulary/random/${level}`, { params: { count } });
  }

  // 🟢 [新增] 访客试玩：公开词库（无需 Token，命中服务端 10 分钟缓存）
  async getPublicRandomVocabulary(level = 'core', count = 30) {
    return this.client.get(`/vocabulary/public/random/${level}`, { params: { count } });
  }

  // ==========================================
  // 4. Lessons (课程管理)
  // ==========================================
  // 获取课程列表
  async getLessons(params = {}) { return this.client.get('/lessons', { params }); }
  
  // 获取单节课详情
  async getLessonById(id) { return this.client.get(`/lessons/${id}`); }
  
  // [Admin] 生成/修复课程
  async generateCurriculum() { return this.client.post('/lessons/generate'); }

  // ==========================================
  // 5. Assignments (作业系统)
  // ==========================================
  // 获取作业列表 (教师/学生通用)
  async getAssignments(params = {}) { return this.client.get('/assignments', { params }); }
  
  // 获取单个作业详情
  async getAssignmentById(id) { return this.client.get(`/assignments/${id}`); }
  
  // [Teacher] 创建作业
  async createAssignment(data) { return this.client.post('/assignments', data); }
  
  // [Student] 提交作业
  async submitAssignment(id, data) { return this.client.post(`/assignments/${id}/submit`, data); }

  // ==========================================
  // 6. Teacher & Classes & Schools (班级与学校)
  // ==========================================
  // Classes
  async getClasses() { return this.client.get('/classes'); }
  async createClass(data) { return this.client.post('/classes', data); }
  
  // Schools
  async getSchools(params = {}) { return this.client.get('/schools', { params }); }
  async createSchool(data) { return this.client.post('/schools', data); }
  async updateSchool(id, data) { return this.client.put(`/schools/${id}`, data); }
  async deleteSchool(id) { return this.client.delete(`/schools/${id}`); }

  // ==========================================
  // 7. Games & Progress (游戏与进度)
  // ==========================================
  // 游戏分数
  async saveGameScore(data) { return this.client.post('/games/score', data); }
  async getLeaderboard(gameType, limit) { return this.client.get('/games/leaderboard', { params: { gameType, limit } }); }
  async getGameScores(gameType, limit) { return this.client.get('/games/scores', { params: { gameType, limit } }); }

  // 学习进度
  async updateLessonProgress(data) { return this.client.post('/progress/lesson', data); }
  async getProgress() { return this.client.get('/progress'); }

  // ==========================================
  // 8. Badges (徽章系统)
  // ==========================================
  async getSystemBadges() { return this.client.get('/badges'); }
  async getUserBadges() { return this.client.get('/badges/user'); }
  
  // 检查徽章 (触发事件)
  async checkBadges(triggerEvent, triggerValue) { 
    return this.client.post('/badges/check', { triggerEvent, triggerValue }); 
  }
  
  // [Admin] 徽章管理
  async createBadge(data) { return this.client.post('/badges', data); }
  async updateBadge(id, data) { return this.client.put(`/badges/${id}`, data); }

  // ==========================================
  // 9. Shop (商店系统)
  // ==========================================
  async getShopItems() { return this.client.get('/shop/items'); }
  async purchaseItem(itemId) { return this.client.post('/shop/purchase', { itemId }); }

  // ==========================================
  // 10. Analytics & Settings (系统设置与统计)
  // ==========================================
  async getGrowthAnalytics() { return this.client.get('/analytics/growth'); }
  async getDashboardAnalytics() { return this.client.get('/analytics/dashboard'); }
  
  async getSettings() { return this.client.get('/settings'); }
  async updateSettings(settings) { return this.client.post('/settings', settings); }
}

export const apiService = new ApiService();
export default apiService;